/**
 * A credential form that stays on the page it is rendered on.
 *
 * Deliberately not the `/sign-up` route, and it does not import it: the
 * invitation token lives in `AcceptInvitation`'s state, and a navigation to a
 * shared auth route loses it. Recovering from that means finding the original
 * message and opening the link again, which is a flow that fails quietly in
 * exactly the common case — the person has already closed it.
 *
 * Sign-in and sign-up share the two fields rather than sitting behind a mode
 * switch. The invitee does not necessarily know whether their address already
 * has an account (§18.2), and asking them to choose is asking a question they
 * cannot answer; the two buttons let them try either without retyping.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import { t } from '../../../i18n';
import { toAuthFailure } from '../../../lib/auth';
import { Button } from '../../../ui/button';
import { Field, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { describeAuthFailure, useAuthSession } from '../../auth/AuthProvider';
import { GoogleButton } from '../../routes/GoogleButton';

export function InlineCredential(): ReactNode {
  const { session } = useAuthSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // No navigation on success, unlike the standalone auth routes: the parent re-renders
  // as the pre-flight for the address that just signed in, still holding the
  // token it read on arrival.
  async function attempt(credential: () => Promise<void>): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await credential();
    } catch (cause) {
      setError(describeAuthFailure(toAuthFailure(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void attempt(() => session.signIn(email, password));
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Field>
        <FieldLabel htmlFor="accept-email">{t('auth.email')}</FieldLabel>
        <Input
          id="accept-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>

      <Field>
        <FieldLabel htmlFor="accept-password">{t('auth.password')}</FieldLabel>
        <Input
          id="accept-password"
          name="password"
          type="password"
          // `new-password`, not `current-password`: the same field submits
          // both buttons, and only this value makes a password manager offer
          // to generate one for the invitee who has no account yet.
          autoComplete="new-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>

      {error !== null && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Button type="submit" disabled={submitting}>
        {t('auth.signIn')}
      </Button>

      <Button
        type="button"
        variant="outline"
        disabled={submitting}
        onClick={() => void attempt(() => session.signUp(email, password))}
      >
        {t('auth.signUp')}
      </Button>

      <GoogleButton
        disabled={submitting}
        onClick={() => void attempt(() => session.signInWithGoogle())}
      />
    </form>
  );
}
